/** Bounded client observations, never authoritative recording verdicts.
 * No raw exception messages or URLs: media URLs contain session credentials. */
export const RECORDING_DIAGNOSTIC_LEVELS = {
  'capture.selected': 'info',
  'recorder.start_failed': 'error',
  'sink.worker_failed': 'error',
  'transform.worker_failed': 'error',
  'user.capture_failed': 'error',
  'assistant.capture_failed': 'error',
  'assistant.prepare_failed': 'warn',
  'upload.lock_failed': 'error',
  'upload.memory_staging': 'warn',
  'upload.connect_failed': 'warn',
  'upload.socket_error': 'warn',
  'recorder.stop_failed': 'warn',
} as const;

export type RecordingDiagnosticCode = keyof typeof RECORDING_DIAGNOSTIC_LEVELS;
export type RecordingDiagnostic = {
  code: RecordingDiagnosticCode;
  path: 'encoded-transform' | 'webcodecs-opus' | 'pcm16' | 'none';
  errorName?: string;
};

const ERROR_NAMES = new Set([
  'Error', 'TypeError', 'ReferenceError', 'SyntaxError', 'RangeError',
  'SecurityError', 'NotSupportedError', 'NotAllowedError', 'InvalidStateError',
  'QuotaExceededError', 'AbortError', 'NetworkError', 'DataCloneError',
]);

export function recordingErrorName(error: unknown): string | undefined {
  if (error === undefined || error === null) return undefined;
  const name = typeof error === 'object' && 'name' in error ? error.name : undefined;
  return typeof name === 'string' && ERROR_NAMES.has(name) ? name : 'Error';
}

/** Validate at the server boundary and copy only the allowlisted fields. */
export function parseRecordingDiagnostic(value: unknown): RecordingDiagnostic | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.code !== 'string' || !Object.hasOwn(RECORDING_DIAGNOSTIC_LEVELS, v.code)) return null;
  if (v.path !== 'encoded-transform' && v.path !== 'webcodecs-opus' && v.path !== 'pcm16' && v.path !== 'none') return null;
  return {
    code: v.code as RecordingDiagnosticCode,
    path: v.path,
    ...(typeof v.errorName === 'string' && ERROR_NAMES.has(v.errorName) ? { errorName: v.errorName } : {}),
  };
}
